import { ErrorComponent } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ApiError,
  USAGE_FILTER_NONE,
  api,
  type UsageReportFilter,
  type UsageReportGroup,
  type UsageReportPeriod,
  type UsageReportRow,
  type UsageSession,
} from "@/lib/api";
import {
  FILTER_SEARCH_KEYS,
  type ActiveFilter,
  reportRetry,
  useActiveFilters,
  useSpendAccess,
} from "@/lib/use-spend-report";
import { PURPOSE_LABELS, purposeColor } from "@/lib/usage-purpose";
import { cn } from "@/lib/utils";
import { formatCostLong } from "@/lib/format";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  X,
} from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";

// Spend attribution for boo-brain: ranked breakdowns that double as filters,
// and the recent conversations. ONE number rules the tab — the ledger's Total
// spend — and every card here adds up to it.
//
// The brain's ledger says WHO and WHAT; the credit ledger says HOW MUCH was
// charged. The two never agree to the cent (different clocks, different
// windows), so the brain's figures are used as shares and scaled onto the
// ledger's model charges: every row is that row's share of what the org was
// actually billed for model usage. Rows past the top N roll into "other" (one
// click opens the full list in place) and the brain's unattributed remainder
// keeps its own row, so the People, Channels and Doing-what cards each add up
// to the ledger's model charges, collapsed or open.
// The ledger's non-model charges (infrastructure, add-on services — which no
// person, channel or activity owns) are a fourth card, and model charges plus
// that card are Total spend. No card prints a total of its own.


// Activity categories are few and fixed, so the card lists every one by
// name; people and channels are open-ended and keep the top-N + rollup.
const PURPOSE_LIMIT = 12;

// Selecting a category re-scopes the whole panel; for these purposes the
// "Doing what" card then shows the category's own finer dimension instead of
// a single redundant row. No in-place expansion — cards stay flat.
const PURPOSE_DETAIL: Partial<
  Record<string, { group: UsageReportGroup; title: string }>
> = {
  heartbeats: { group: "heartbeat-task", title: "Scheduled tasks" },
  workflow: { group: "workflow-run", title: "Workflows" },
  subagents: { group: "subagent-kind", title: "Helpers" },
};

type ExpandableCard = "user" | "channel" | "detail";

// The People and Channels cards' unattributed rows; picking one filters with
// USAGE_FILTER_NONE, and the chip reuses the row's label.
const UNATTRIBUTED_USER_LABEL = "Scheduled & background work";
const UNATTRIBUTED_CHANNEL_LABEL = "Outside channels";

const TOP_N = 6;
// Open-ended cards fetch this many rows up front (the proxy's ceiling): the
// collapsed card shows the top N and already knows what "Show all" opens.
const FULL_LIMIT = 100;
const ENTRIES_PAGE = 25;


function useSpendSummary(
  containerId: string,
  period: UsageReportPeriod,
  groupBy: UsageReportGroup,
  filter: UsageReportFilter,
  opts?: { limit?: number; enabled?: boolean },
) {
  const limit = opts?.limit ?? TOP_N;
  return useQuery({
    queryKey: ["usage-report", containerId, period, groupBy, limit, filter],
    queryFn: () =>
      api.containers.usage.report.summary(containerId, period, groupBy, {
        ...filter,
        limit,
      }),
    enabled: opts?.enabled ?? true,
    retry: reportRetry,
    // A filter click must never collapse the layout into skeletons — hold the
    // previous numbers, dimmed, until the rescope lands.
    placeholderData: keepPreviousData,
  });
}

/** The credit ledger's figures for the tab's period: what the cards below
 * add up to. `ai` is model charges; the rest is the fourth card. */
export interface LedgerSplit {
  total: number;
  ai: number;
  infrastructure: number;
  services: number;
  base: number;
}

// Brain dollars → ledger dollars. The brain's unscoped window total maps onto
// the ledger's model charges for the same window; every brain figure on the
// tab is scaled by that one factor. Without a usable denominator (no ledger
// charges yet, or the brain reporting nothing) figures pass through unscaled.
function useLedgerScale(containerId: string, period: UsageReportPeriod, ledger: LedgerSplit) {
  const windowTotals = useQuery({
    queryKey: ["usage-report-totals", containerId, period],
    queryFn: () => api.containers.usage.report.totals(containerId, period),
    retry: reportRetry,
    placeholderData: keepPreviousData,
  });
  const windowBrainTotal = windowTotals.data?.totals.costUsd ?? 0;
  const scale = ledger.ai > 0 && windowBrainTotal > 0 ? ledger.ai / windowBrainTotal : 1;
  return { scale, windowTotals };
}

/** `chart` renders between the cards and the conversations, or on its own
 * wherever the panel doesn't render. */
export function SpendAttributionSection({
  containerId,
  period,
  ledger,
  chart,
}: {
  containerId: string;
  period: UsageReportPeriod;
  ledger: LedgerSplit;
  chart: ReactNode;
}) {
  // The wrapper keeps hook order legal for the panel.
  if (useSpendAccess(containerId) !== "on") return chart;
  return <SpendPanel containerId={containerId} period={period} ledger={ledger} chart={chart} />;
}

function SpendPanel({
  containerId,
  period,
  ledger,
  chart,
}: {
  containerId: string;
  period: UsageReportPeriod;
  ledger: LedgerSplit;
  chart: ReactNode;
}) {
  const navigate = useNavigate();
  const { filters, filter, searchString } = useActiveFilters();

  // Every state change PUSHES history — Back undoes the last filter step
  // instead of leaving the page.
  const patchSearch = (patch: Record<string, string | undefined>) =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev };
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) delete next[key];
          else next[key] = value;
        }
        return next;
      },
    });

  // Clicking a row scopes to it; clicking the same row again scopes back out.
  const toggleFilter = (next: ActiveFilter) => {
    const key = FILTER_SEARCH_KEYS[next.param];
    patchSearch({
      [key]: searchString(key) === next.value ? undefined : next.value,
    });
  };
  const removeFilter = (param: ActiveFilter["param"]) =>
    patchSearch({ [FILTER_SEARCH_KEYS[param]]: undefined });
  const clearFilters = () =>
    patchSearch(
      Object.fromEntries(
        Object.values(FILTER_SEARCH_KEYS).map((key) => [key, undefined]),
      ),
    );
  const { scale, windowTotals } = useLedgerScale(containerId, period, ledger);
  const people = useSpendSummary(containerId, period, "user", filter, { limit: FULL_LIMIT });
  const channels = useSpendSummary(containerId, period, "channel", filter, { limit: FULL_LIMIT });
  const purposes = useSpendSummary(containerId, period, "purpose", filter, { limit: PURPOSE_LIMIT });
  // With a category selected, the "Doing what" card shows that category's own
  // dimension (workflow names, task names, helper kinds).
  const purposeDetail =
    filter.purpose !== undefined ? PURPOSE_DETAIL[filter.purpose] : undefined;
  const detail = useSpendSummary(
    containerId,
    period,
    purposeDetail?.group ?? "purpose",
    filter,
    { limit: FULL_LIMIT, enabled: purposeDetail !== undefined },
  );

  // Which open-ended cards show their full list. Local, not in the URL: it is
  // a view preference, not a scope.
  const [expanded, setExpanded] = useState<Partial<Record<ExpandableCard, boolean>>>({});
  const toggleExpanded = (card: ExpandableCard) =>
    setExpanded((prev) => ({ ...prev, [card]: prev[card] !== true }));

  // The one conversation the view is scoped to, if any: its own rollup over
  // the whole conversation (other filters aside) labels the chip and fills
  // the header that replaces the conversation list.
  const threadId = filter.threadId;
  const thread = useQuery({
    queryKey: ["usage-report-thread", containerId, period, threadId],
    queryFn: () =>
      api.containers.usage.report.sessions(containerId, period, { threadId, limit: 1 }),
    enabled: threadId !== undefined,
    retry: reportRetry,
  });
  const threadSession = thread.data?.sessions.find((s) => s.threadId === threadId);

  // Channel ids in the ledger are bare provider ids — resolve display names
  // from the channel list (shares the ChannelsTab cache key).
  const channelsList = useQuery({
    queryKey: ["channels", containerId],
    queryFn: () => api.containers.channels.list(containerId),
  });
  const channelName = useMemo(() => {
    const map = new Map<string, string>();
    for (const ch of channelsList.data?.channels ?? []) {
      if (ch.displayName) map.set(ch.channelOrSpaceId, ch.displayName);
    }
    return map;
  }, [channelsList.data]);
  const userName = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of people.data?.rows ?? []) {
      if (row.displayName) map.set(row.key, row.displayName);
    }
    return map;
  }, [people.data]);

  const labelForFilter = (f: ActiveFilter): string => {
    if (f.param === "userId" && f.value === USAGE_FILTER_NONE) return UNATTRIBUTED_USER_LABEL;
    if (f.param === "channelId" && f.value === USAGE_FILTER_NONE) {
      return UNATTRIBUTED_CHANNEL_LABEL;
    }
    if (f.param === "userId") return userName.get(f.value) ?? f.value;
    if (f.param === "channelId") return channelName.get(f.value) ?? f.value;
    if (f.param === "purpose") return PURPOSE_LABELS[f.value] ?? f.value;
    if (threadSession === undefined) return "This conversation";
    return `This conversation · ${sessionWhere(threadSession, channelName)}, ${shortDate(threadSession.lastTs)}`;
  };

  // The two expected whole-section states; anything else degrades per card.
  const sectionError = [windowTotals, people, channels, purposes]
    .map((q) => q.error)
    .find(
      (err) =>
        err instanceof ApiError &&
        (err.code === "brain_outdated" ||
          err.code === "brain_unreachable" ||
          err.status === 403),
    );
  // The proxy disagreeing with the row's permissions (role changed since the
  // list loaded): the panel simply doesn't exist for this viewer. Scoped to a
  // conversation, a 403 is the proxy refusing that one scope: keep the scope
  // bar so the chip can be removed instead of the panel vanishing.
  if (sectionError instanceof ApiError && sectionError.status === 403) {
    if (threadId === undefined) return chart;
    return (
      <section className="space-y-3">
        <h2 className="text-base font-semibold">Where spend goes</h2>
        <ScopeBar
          filters={filters.map((f) => ({ ...f, label: labelForFilter(f) }))}
          onRemove={removeFilter}
          onClearAll={clearFilters}
        />
        <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
          Spend for a single conversation isn't available here yet.
        </p>
        {chart}
      </section>
    );
  }
  if (sectionError !== undefined) {
    return (
      <section className="space-y-3">
        <h2 className="text-base font-semibold">Where spend goes</h2>
        <ReportUnavailable error={sectionError} />
        {chart}
      </section>
    );
  }

  // Unscoped, the three model cards each total to the ledger's model charges
  // and the fourth card carries the rest. Scoped to a person, channel or
  // activity, the ledger has no matching number: the cards total to that
  // scope's share of model charges and the fourth card steps aside.
  const scoped = filters.length > 0;
  const scopedBrainTotal = people.data?.totals.costUsd ?? 0;
  const modelTotal = scoped ? scopedBrainTotal * scale : ledger.ai;
  const extras = ledger.infrastructure + ledger.services + ledger.base;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">Where spend goes</h2>

      <ScopeBar
        filters={filters.map((f) => ({ ...f, label: labelForFilter(f) }))}
        onRemove={removeFilter}
        onClearAll={clearFilters}
      />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Column title="People" state={columnState(people)}>
          {(rows, total, unattributed) => (
            <PlainRows
              rows={rows}
              brainTotal={total}
              total={modelTotal}
              scale={scale}
              otherLabel="Everyone else"
              nameFor={(r) => r.displayName ?? r.key}
              unattributed={unattributed}
              unattributedLabel={UNATTRIBUTED_USER_LABEL}
              selectedKey={filter.userId}
              onPick={(r) => toggleFilter({ param: "userId", value: r.key })}
              onPickUnattributed={() =>
                toggleFilter({ param: "userId", value: USAGE_FILTER_NONE })
              }
              collapsedCount={TOP_N}
              expanded={expanded.user === true}
              onToggleExpanded={() => toggleExpanded("user")}
            />
          )}
        </Column>
        <Column title="Channels" state={columnState(channels)}>
          {(rows, total, unattributed) => (
            <PlainRows
              rows={rows}
              brainTotal={total}
              total={modelTotal}
              scale={scale}
              otherLabel="Other channels"
              nameFor={(r) => channelName.get(r.key) ?? r.key}
              unattributed={unattributed}
              unattributedLabel={UNATTRIBUTED_CHANNEL_LABEL}
              selectedKey={filter.channelId}
              onPick={(r) => toggleFilter({ param: "channelId", value: r.key })}
              onPickUnattributed={() =>
                toggleFilter({ param: "channelId", value: USAGE_FILTER_NONE })
              }
              collapsedCount={TOP_N}
              expanded={expanded.channel === true}
              onToggleExpanded={() => toggleExpanded("channel")}
            />
          )}
        </Column>
        <Column
          title={purposeDetail?.title ?? "Doing what"}
          state={columnState(purposeDetail !== undefined ? detail : purposes)}
          onBack={
            purposeDetail !== undefined
              ? () => removeFilter("purpose")
              : undefined
          }
        >
          {(rows, total) => (
            <PlainRows
              rows={rows}
              brainTotal={total}
              total={modelTotal}
              scale={scale}
              otherLabel="Other activity"
              nameFor={(r) =>
                purposeDetail !== undefined
                  ? (r.label ?? r.key)
                  : (PURPOSE_LABELS[r.key] ?? r.key)
              }
              selectedKey={purposeDetail !== undefined ? undefined : filter.purpose}
              onPick={
                purposeDetail !== undefined
                  ? undefined
                  : (r) => toggleFilter({ param: "purpose", value: r.key })
              }
              swatchFor={
                purposeDetail !== undefined ? undefined : (r) => purposeColor(r.key)
              }
              // Categories are few and fixed: listed in full, never folded.
              // A category's own dimension is open-ended and folds like People.
              collapsedCount={purposeDetail !== undefined ? TOP_N : PURPOSE_LIMIT}
              expanded={expanded.detail === true}
              onToggleExpanded={
                purposeDetail !== undefined ? () => toggleExpanded("detail") : undefined
              }
            />
          )}
        </Column>
        {!scoped && <ExtrasCard ledger={ledger} total={extras} />}
      </div>

      {chart}

      {threadId !== undefined ? (
        <ConversationHeader
          loading={thread.isLoading}
          error={thread.isError}
          session={threadSession}
          scale={scale}
          channelName={channelName}
          onBack={() => removeFilter("threadId")}
        />
      ) : (
        <RecentConversations
          containerId={containerId}
          period={period}
          filter={filter}
          scale={scale}
          channelName={channelName}
          onFilterThread={(session) =>
            toggleFilter({ param: "threadId", value: session.threadId })
          }
        />
      )}

      <p className="text-xs text-muted-foreground">
        People, Channels and Doing what each share out the same model charges
        by measured usage. Together with Infrastructure &amp; add-ons they make
        Total spend above.
      </p>
    </section>
  );
}

// The one place filter state lives on screen: impossible to miss, one click
// to undo any of it. Rendered only while something is filtered.
function ScopeBar({
  filters,
  onRemove,
  onClearAll,
}: {
  filters: Array<ActiveFilter & { label: string }>;
  onRemove: (param: ActiveFilter["param"]) => void;
  onClearAll: () => void;
}) {
  // Idle state keeps the bar's height so the first filter never shifts layout.
  if (filters.length === 0) {
    return (
      <div className="flex min-h-11 flex-wrap items-center rounded-lg bg-secondary px-3 py-2">
        <span className="text-xs text-muted-foreground">
          Showing everything — click any row below to focus the view.
        </span>
      </div>
    );
  }
  return (
    <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-lg bg-secondary px-3 py-2">
      <span className="text-xs text-muted-foreground">Filtered to</span>
      {filters.map((f) => (
        <Button
          key={f.param}
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 bg-background font-normal"
          onClick={() => onRemove(f.param)}
        >
          <span className={cn("truncate", f.param === "threadId" ? "max-w-80" : "max-w-48")}>
            {f.label}
          </span>
          <X className="size-3.5 text-muted-foreground" />
        </Button>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-muted-foreground"
        onClick={onClearAll}
      >
        Clear all
      </Button>
    </div>
  );
}

// ── Breakdown columns ───────────────────────────────────────────────────────

interface ColumnState {
  loading: boolean;
  fetching: boolean;
  error: boolean;
  rows: readonly UsageReportRow[];
  total: number;
  unattributed: number;
}

function columnState(query: {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  data?: {
    rows: UsageReportRow[];
    totals: { costUsd: number };
    unattributed?: { costUsd: number };
  };
}): ColumnState {
  return {
    loading: query.isLoading,
    fetching: query.isFetching,
    error: query.isError,
    rows: query.data?.rows ?? [],
    total: query.data?.totals.costUsd ?? 0,
    unattributed: query.data?.unattributed?.costUsd ?? 0,
  };
}

function Column({
  title,
  caption,
  state,
  onBack,
  children,
}: {
  title: string;
  caption?: string;
  state: ColumnState;
  /** Back affordance where the state changed: shown when the card is scoped
   * to a selection, one click restores the unscoped card. */
  onBack?: () => void;
  children: (
    rows: readonly UsageReportRow[],
    total: number,
    unattributed: number,
  ) => ReactNode;
}) {
  return (
    <div className={cn("rounded-lg border p-4", state.fetching && "opacity-60 transition-opacity")}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        {onBack !== undefined && (
          <button
            type="button"
            onClick={onBack}
            title="Back to all categories"
            className="-ml-1 rounded p-0.5 hover:bg-muted"
          >
            <ArrowLeft className="size-3.5" />
          </button>
        )}
        {title}
      </div>
      {state.loading ? (
        <div className="mt-3 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : state.error ? (
        <p className="mt-3 text-sm text-muted-foreground">Couldn't load this breakdown.</p>
      ) : state.rows.length === 0 && state.unattributed <= 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Nothing in this window.</p>
      ) : (
        children(state.rows, state.total, state.unattributed)
      )}
      {caption !== undefined && (
        <p className="mt-3 text-[11px] leading-snug text-muted-foreground">{caption}</p>
      )}
    </div>
  );
}

function PlainRows({
  rows,
  brainTotal,
  total,
  scale,
  otherLabel,
  nameFor,
  unattributed,
  unattributedLabel,
  onPick,
  onPickUnattributed,
  selectedKey,
  swatchFor,
  collapsedCount,
  expanded,
  onToggleExpanded,
}: {
  rows: readonly UsageReportRow[];
  /** The brain's total for this scope (brain dollars) — what rows + other +
   * unattributed reach before scaling. */
  brainTotal: number;
  /** What the card adds up to, in ledger dollars; the base for every bar. */
  total: number;
  /** Brain dollars → ledger dollars; applied to every brain figure shown. */
  scale: number;
  /** Rows not listed, rolled up so the card still reaches `total`. */
  otherLabel: string;
  nameFor: (row: UsageReportRow) => string;
  /** The brain-reported remainder with no value for this dimension. */
  unattributed?: number;
  unattributedLabel?: string;
  onPick?: (row: UsageReportRow) => void;
  /** Scope to the unattributed remainder (filter value USAGE_FILTER_NONE). */
  onPickUnattributed?: () => void;
  /** The row currently scoping the view; clicking it again scopes back out.
   * USAGE_FILTER_NONE selects the unattributed row. */
  selectedKey?: string;
  swatchFor?: (row: UsageReportRow) => string | undefined;
  /** Rows listed while folded; the rest roll into `otherLabel`. */
  collapsedCount: number;
  expanded?: boolean;
  /** Present when the card can open to every fetched row. */
  onToggleExpanded?: () => void;
}) {
  const expandable = onToggleExpanded !== undefined && rows.length > collapsedCount;
  const open = expandable && expanded === true;
  const visible = open ? rows : rows.slice(0, collapsedCount);
  const shown = visible.reduce((acc, r) => acc + r.costUsd, 0);
  // Open or folded, the remainder is taken from the same brain total, so the
  // card sums to the same figure either way. Open, it is only what lies past
  // the fetch ceiling.
  const other = (brainTotal - shown - (unattributed ?? 0)) * scale;
  const share = (cost: number) => (total > 0 ? cost / total : 0);
  const unattributedSelected = selectedKey === USAGE_FILTER_NONE;
  const showAllLabel =
    rows.length >= FULL_LIMIT ? `Show top ${rows.length}` : `Show all ${rows.length}`;

  const items = visible.map((row) => {
    const selected = selectedKey !== undefined && row.key === selectedKey;
    const body = (
      <BreakdownRow
        name={nameFor(row)}
        cost={row.costUsd * scale}
        share={share(row.costUsd * scale)}
        selected={selected}
        swatch={swatchFor?.(row)}
      />
    );
    return (
      <li key={row.key}>
        {onPick !== undefined ? (
          <button
            type="button"
            onClick={() => onPick(row)}
            className={cn(
              // block, not the button default inline-block: inline layout
              // leaves a baseline strip inside the li where the cursor
              // flips back to default mid-row.
              "-mx-1.5 block w-[calc(100%+0.75rem)] rounded px-1.5 py-[5px] text-left transition-colors",
              selected ? "bg-muted" : "hover:bg-muted/50",
            )}
            title={selected ? "Clear this filter" : "Filter to this"}
          >
            {body}
          </button>
        ) : (
          <div className="py-[5px]">{body}</div>
        )}
      </li>
    );
  });

  return (
    // Rows are contiguous (padding carries the visual rhythm, no list gaps):
    // an interactive card is one stable pointer region instead of a cursor
    // flickering across every gap, and the whole row height is clickable.
    <div className="mt-1.5">
      {open ? (
        // Open, the list scrolls inside the card past ~11 rows so the grid
        // row stays a sane height; the side padding keeps row hover fills
        // unclipped, the rule marks where the scrolling list ends.
        <div className="-mx-1.5 max-h-[27rem] overflow-y-auto overscroll-contain border-b px-1.5 pb-1">
          <ul>{items}</ul>
        </div>
      ) : (
        <ul>{items}</ul>
      )}
      <ul>
        {other > 0.005 && (
          <li>
            {expandable && !open ? (
              <button
                type="button"
                onClick={onToggleExpanded}
                className="-mx-1.5 block w-[calc(100%+0.75rem)] rounded px-1.5 py-[5px] text-left transition-colors hover:bg-muted/50"
                title={showAllLabel}
              >
                <BreakdownRow
                  name={otherLabel}
                  hint={`${rows.length - visible.length} more`}
                  muted
                  cost={other}
                  share={share(other)}
                />
              </button>
            ) : (
              <div className="py-[5px]">
                <BreakdownRow name={otherLabel} muted cost={other} share={share(other)} />
              </div>
            )}
          </li>
        )}
        {unattributed !== undefined && unattributed > 0 && (
          <li>
            {onPickUnattributed !== undefined ? (
              <button
                type="button"
                onClick={onPickUnattributed}
                className={cn(
                  "-mx-1.5 block w-[calc(100%+0.75rem)] rounded px-1.5 py-[5px] text-left transition-colors",
                  unattributedSelected ? "bg-muted" : "hover:bg-muted/50",
                )}
                title={unattributedSelected ? "Clear this filter" : "Filter to this"}
              >
                <BreakdownRow
                  name={unattributedLabel ?? "Unattributed"}
                  muted
                  selected={unattributedSelected}
                  cost={unattributed * scale}
                  share={share(unattributed * scale)}
                />
              </button>
            ) : (
              <div className="py-[5px]">
                <BreakdownRow
                  name={unattributedLabel ?? "Unattributed"}
                  muted
                  cost={unattributed * scale}
                  share={share(unattributed * scale)}
                />
              </div>
            )}
          </li>
        )}
      </ul>
      {expandable && (
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={open}
          className="-mx-1.5 mt-1 flex h-8 items-center gap-1 rounded px-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
        >
          {open ? "Show less" : showAllLabel}
          {open ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
      )}
    </div>
  );
}

// The fourth card: the ledger's non-model charges, straight from the credit
// ledger (no brain figures, no scaling). Its total plus Model charges is
// Total spend.
function ExtrasCard({ ledger, total }: { ledger: LedgerSplit; total: number }) {
  const rows = [
    { key: "infrastructure", name: "Infrastructure", cost: ledger.infrastructure },
    { key: "services", name: "Add-on services", cost: ledger.services },
    { key: "base", name: "Base fee", cost: ledger.base },
  ].filter((r) => r.cost > 0.005);
  return (
    <div className="rounded-lg border p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        Infrastructure &amp; add-ons
      </div>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Nothing in this window.</p>
      ) : (
        <ul className="mt-1.5">
          {rows.map((r) => (
            <li key={r.key} className="py-[5px]">
              <BreakdownRow name={r.name} cost={r.cost} share={total > 0 ? r.cost / total : 0} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BreakdownRow({
  name,
  cost,
  share,
  muted,
  selected,
  swatch,
  hint,
}: {
  name: string;
  cost: number;
  share: number;
  muted?: boolean;
  selected?: boolean;
  /** Color key tying the row to its series in the daily chart. */
  swatch?: string;
  /** Quiet trailing note after the name (e.g. "14 more"). */
  hint?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-baseline gap-1">
          {swatch !== undefined && (
            <span
              aria-hidden
              className="mr-0.5 size-2 shrink-0 self-center rounded-[2px]"
              style={{ background: swatch }}
            />
          )}
          <span
            className={cn("truncate text-sm", muted ? "text-muted-foreground" : "font-medium")}
            title={name}
          >
            {name}
          </span>
          {hint !== undefined && (
            <span className="shrink-0 text-xs text-muted-foreground">· {hint}</span>
          )}
          {hint !== undefined && (
            <ChevronDown className="size-3 shrink-0 self-center text-muted-foreground" />
          )}
          {selected === true && (
            <X className="size-3 shrink-0 self-center text-muted-foreground" />
          )}
        </span>
        <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {formatCostLong(cost)}
        </span>
      </div>
      <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", muted && "opacity-40")}
          style={{
            width: `${Math.max(share * 100, cost > 0 ? 0.75 : 0)}%`,
            background: "var(--chart-5)",
          }}
        />
      </div>
    </div>
  );
}

// ── Recent conversations ────────────────────────────────────────────────────
// One row per conversation: when, where, who, what it cost. Clicking a row
// scopes the whole view to that conversation. Deliberately no step-by-step
// detail — a row is a session, not a transcript.

function RecentConversations({
  containerId,
  period,
  filter,
  scale,
  channelName,
  onFilterThread,
}: {
  containerId: string;
  period: UsageReportPeriod;
  filter: UsageReportFilter;
  /** Brain dollars → ledger dollars, same factor as the cards. */
  scale: number;
  channelName: Map<string, string>;
  /** Scope the whole view to one conversation. */
  onFilterThread: (session: UsageSession) => void;
}) {
  // First page via the query cache; older pages accumulate locally and reset
  // whenever the scope (and so the query key) changes.
  const [older, setOlder] = useState<{ scope: string; pages: UsageSession[][] }>(
    { scope: "", pages: [] },
  );
  const [loadingOlder, setLoadingOlder] = useState(false);
  const scope = JSON.stringify([containerId, period, filter]);

  const firstPage = useQuery({
    queryKey: ["usage-sessions", containerId, period, filter],
    queryFn: () =>
      api.containers.usage.report.sessions(containerId, period, {
        ...filter,
        limit: ENTRIES_PAGE,
      }),
    retry: reportRetry,
    placeholderData: keepPreviousData,
  });

  const [olderError, setOlderError] = useState(false);
  const pages = older.scope === scope ? older.pages : [];
  const sessions = [...(firstPage.data?.sessions ?? []), ...pages.flat()];
  const lastLoaded = pages.length > 0 ? pages[pages.length - 1] : firstPage.data?.sessions;
  const oldest = sessions[sessions.length - 1];
  const mayHaveOlder =
    (pages.length === 0
      ? firstPage.data?.nextBeforeTs !== undefined
      : (lastLoaded?.length ?? 0) === ENTRIES_PAGE) && oldest !== undefined;

  const loadOlder = async () => {
    if (oldest === undefined) return;
    setLoadingOlder(true);
    setOlderError(false);
    try {
      const page = await api.containers.usage.report.sessions(containerId, period, {
        ...filter,
        limit: ENTRIES_PAGE,
        // Both cursor halves: the thread id breaks same-millisecond ties.
        beforeTs: oldest.lastTs,
        beforeThread: oldest.threadId,
      });
      setOlder({ scope, pages: [...pages, page.sessions] });
    } catch {
      setOlderError(true);
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <div className="rounded-lg border">
      <div className="px-4 pt-4 text-xs uppercase tracking-wide text-muted-foreground">
        Recent conversations
      </div>
      {firstPage.isLoading ? (
        <div className="space-y-3 p-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : firstPage.isError ? (
        <p className="p-4 text-sm text-muted-foreground">Couldn't load recent conversations.</p>
      ) : sessions.length === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Nothing in this window.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">Last active</TableHead>
                <TableHead>Where</TableHead>
                <TableHead>Who</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="w-10 pr-4">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((session) => (
                <SessionRow
                  key={session.threadId}
                  session={session}
                  scale={scale}
                  channelName={channelName}
                  onFilterThread={onFilterThread}
                />
              ))}
            </TableBody>
          </Table>
          {mayHaveOlder && (
            <div className="border-t p-2 text-center">
              <Button variant="ghost" size="sm" disabled={loadingOlder} onClick={loadOlder}>
                {loadingOlder ? "Loading…" : olderError ? "Couldn't load older — retry" : "Load older"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function sessionWhere(session: UsageSession, channelName: Map<string, string>): string {
  if (session.heartbeatTaskId !== undefined) return `Scheduled task · ${session.heartbeatTaskId}`;
  if (session.channelId !== undefined) {
    return channelName.get(session.channelId) ?? session.channelId;
  }
  if (session.provider === "dashboard_chat") return "Dashboard chat";
  return "Direct message";
}

function SessionRow({
  session,
  scale,
  channelName,
  onFilterThread,
}: {
  session: UsageSession;
  scale: number;
  channelName: Map<string, string>;
  onFilterThread: (session: UsageSession) => void;
}) {
  const when = new Date(session.lastTs);
  const today = new Date().toDateString() === when.toDateString();
  const whenLabel = today
    ? when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    : when.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
      " " +
      when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const who = sessionWho(session);
  const where = sessionWhere(session, channelName);
  const focus = () => onFilterThread(session);

  return (
    <TableRow
      // cursor-[inherit] on descendants: WebKit won't carry the row's pointer
      // over cell text otherwise.
      className="group cursor-pointer [&_*]:cursor-[inherit]"
      title="Focus the whole view on this conversation"
      role="button"
      tabIndex={0}
      onClick={focus}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          focus();
        }
      }}
    >
      <TableCell className="whitespace-nowrap pl-4 text-muted-foreground">{whenLabel}</TableCell>
      <TableCell className="max-w-56">
        <WhereLink session={session} where={where} />
      </TableCell>
      <TableCell className="max-w-56 truncate">
        {session.participants.length > 0 ? (
          who
        ) : (
          <span className="text-muted-foreground">{who}</span>
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
        {formatCostLong(session.costUsd * scale)}
      </TableCell>
      <TableCell className="w-10 pr-4">
        <ChevronRight className="size-4 text-muted-foreground/60 transition-[translate,color] duration-150 group-hover:translate-x-0.5 group-hover:text-foreground" />
      </TableCell>
    </TableRow>
  );
}

// A scheduled task's ticks share one thread, so its rollup spans many runs —
// say so instead of leaving the rollup silent.
function sessionWho(session: UsageSession): string {
  if (session.participants.length > 0) {
    return session.participants.map((p) => p.displayName ?? p.userId).join(", ");
  }
  if (session.heartbeatTaskId !== undefined) {
    return `${session.runs} ${session.runs === 1 ? "run" : "runs"}`;
  }
  return "—";
}

function WhereLink({ session, where }: { session: UsageSession; where: string }) {
  if (session.link === undefined) return <div className="truncate">{where}</div>;
  return (
    <a
      href={session.link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex max-w-full items-center gap-1 underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-current"
      title="Open in Slack"
      onClick={(event) => event.stopPropagation()}
    >
      <span className="truncate">{where}</span>
      <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
    </a>
  );
}

function shortDate(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dateTime(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Stands in for the conversation list while the view is scoped to one
// conversation: where, who, when, how many runs, what it cost. Never the
// messages, never per-turn rows.
function ConversationHeader({
  loading,
  error,
  session,
  scale,
  channelName,
  onBack,
}: {
  loading: boolean;
  error: boolean;
  /** Absent once loaded: no activity from this conversation in the window. */
  session?: UsageSession;
  scale: number;
  channelName: Map<string, string>;
  onBack: () => void;
}) {
  return (
    <div className="rounded-lg border">
      <div className="flex items-center gap-1.5 px-4 pt-4 text-xs uppercase tracking-wide text-muted-foreground">
        <button
          type="button"
          onClick={onBack}
          title="Back to all conversations"
          className="-ml-1 rounded p-0.5 hover:bg-muted"
        >
          <ArrowLeft className="size-3.5" />
        </button>
        This conversation
      </div>
      {loading ? (
        <div className="space-y-3 p-4">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      ) : error ? (
        <p className="p-4 text-sm text-muted-foreground">Couldn't load this conversation.</p>
      ) : session === undefined ? (
        <p className="p-4 text-sm text-muted-foreground">
          No activity from this conversation in this window.
        </p>
      ) : (
        <div className="px-4 pb-4 pt-2">
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
            <div className="min-w-0 max-w-full text-base font-semibold">
              <WhereLink session={session} where={sessionWhere(session, channelName)} />
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {formatCostLong(session.costUsd * scale)}
            </div>
          </div>
          <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
            <ConversationFact label="Who took part">
              {session.participants.length > 0 ? (
                sessionWho(session)
              ) : (
                <span className="text-muted-foreground">
                  {session.heartbeatTaskId !== undefined ? "Started on a schedule" : "—"}
                </span>
              )}
            </ConversationFact>
            <ConversationFact label="First activity">{dateTime(session.firstTs)}</ConversationFact>
            <ConversationFact label="Last activity">{dateTime(session.lastTs)}</ConversationFact>
            <ConversationFact label="Runs">{session.runs.toLocaleString()}</ConversationFact>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            Covers this conversation's activity in the selected window.
          </p>
        </div>
      )}
    </div>
  );
}

function ConversationFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm tabular-nums [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

// The two expected non-data states get plain words; anything else keeps the
// standard error surface.
function ReportUnavailable({ error }: { error: unknown }) {
  if (error instanceof ApiError && error.code === "brain_outdated") {
    return (
      <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        This Boo doesn't report spend detail yet — it starts with its next
        update.
      </p>
    );
  }
  if (error instanceof ApiError && error.code === "brain_unreachable") {
    return (
      <p className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        Can't reach this Boo right now. Spend detail will be back once it is.
      </p>
    );
  }

  return <ErrorComponent error={error ?? "Failed to load spend detail"} />;
}
